import React, { useState } from 'react';
import { List, Button, Tag, Typography, Spin, message } from 'antd';
import { PaperClipOutlined, UpOutlined, DownOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { followAPI } from '../services/follow-api';
import { Linkify } from '../utils/linkify';
import { canEditFollow, canDeleteFollow, FollowAuthUser } from '../utils/auth';

const { Text, Paragraph } = Typography;

export type FollowHistoryAttachment = {
  attachfileno: number;
  name: string;
  size: number;
  type: string;
};

export type FollowHistoryItem = {
  id: number;
  date: string;
  content: string;
  representative: string;
  /** フォローの担当者No（自フォロー判定に使用。API の生値をそのまま受けるため string も許容） */
  contactuserno?: number | string;
  /** フォローの作成者No（自フォロー判定に使用。担当者でなくても作成者なら編集・削除可） */
  createuserno?: number | string;
  provisional?: boolean;
  plantodo?: boolean;
  hasAttachment?: boolean;
  attachments?: FollowHistoryAttachment[];
};

interface FollowHistoryPanelProps {
  followHistory: FollowHistoryItem[];
  isLoading: boolean;
  onAttachmentLoad: (id: number, attachments: FollowHistoryAttachment[]) => void;
  onAttachmentViewClick: (attachments: FollowHistoryAttachment[], index: number) => void;
  onDeleteClick: (id: number) => void;
  onEditClick: (id: number) => Promise<void>;
  /** ログインユーザー情報（フォロー編集・削除の権限判定に使用） */
  user?: FollowAuthUser;
}

const FollowHistoryPanel: React.FC<FollowHistoryPanelProps> = ({
  followHistory,
  isLoading,
  onAttachmentLoad,
  onAttachmentViewClick,
  onDeleteClick,
  onEditClick,
  user,
}) => {
  const [expandedFollowItems, setExpandedFollowItems] = useState<Set<number>>(new Set());

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '32px'
      }}>
        <Spin size="large" />
        <div style={{ marginLeft: '16px' }}>フォロー履歴を読み込み中...</div>
      </div>
    );
  }

  if (!followHistory || followHistory.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '32px',
        background: '#fff',
        borderRadius: '4px',
        marginTop: '16px'
      }}>
        <Text>フォロー履歴がありません</Text>
      </div>
    );
  }

  return (
    <List
      itemLayout="vertical"
      dataSource={followHistory}
      renderItem={(item, index) => {
        // 編集ボタン：全フォロー編集権限あり、または自分のフォロー（担当 or 作成）のみ表示
        const showEdit = canEditFollow(user, item.contactuserno, item.createuserno);
        // 削除ボタン：フォロー削除権限あり、または自分のフォロー（担当 or 作成）のみ表示
        const showDelete = canDeleteFollow(user, item.contactuserno, item.createuserno);
        // 削除・編集とも非表示の場合はアクションバー（と展開トグル）ごと表示しない
        const showActions = showDelete || showEdit;
        return (
        <List.Item
          style={{
            padding: '12px 16px',
            marginBottom: '8px',
            background: '#fff',
            borderRadius: '4px',
            border: '1px solid #f0f0f0',
          }}
        >
          <div style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Text strong>{item.date}</Text>
                {item.provisional && (
                  <Tag color="orange" style={{ fontSize: 11, padding: '0 4px', lineHeight: '18px', margin: '0 0 0 8px' }}>仮</Tag>
                )}
                {item.plantodo && (
                  <Tag style={{ fontSize: 11, padding: '0 4px', lineHeight: '18px', margin: '0 0 0 4px', backgroundColor: '#f6ffed', borderColor: '#52c41a', color: '#52c41a' }}>未</Tag>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {item.hasAttachment && (
                  <Button
                    type="text"
                    size="small"
                    icon={<PaperClipOutlined style={{ color: '#1677ff' }} />}
                    style={{ backgroundColor: '#e6f4ff' }}
                    onClick={async () => {
                      let attachments = item.attachments;
                      if (!attachments) {
                        try {
                          const res = await followAPI.getAttachmentList({ type: 1, targetno: item.id });
                          attachments = (res.attachfilelist || []).map((file: any) => ({
                            attachfileno: file.attachfileno,
                            name: file.attachfilename || file.filename || file.name || '',
                            size: parseFloat((file.attachfilesize || '0').replace(/,/g, '')) * 1024,
                            type: file.contentType || file.filetype || file.type || ''
                          }));
                          onAttachmentLoad(item.id, attachments!);
                        } catch (error) {
                          console.error('添付ファイル取得エラー:', error);
                          message.error('添付ファイルの取得に失敗しました');
                          return;
                        }
                      }
                      onAttachmentViewClick(attachments || [], index);
                    }}
                  />
                )}
                <Text type="secondary">{item.representative}</Text>
                {showActions && (
                  <Button
                    type="text"
                    icon={expandedFollowItems.has(item.id) ? <UpOutlined /> : <DownOutlined />}
                    onClick={() => {
                      setExpandedFollowItems(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has(item.id)) {
                          newSet.delete(item.id);
                        } else {
                          newSet.add(item.id);
                        }
                        return newSet;
                      });
                    }}
                    style={{ padding: '4px 8px' }}
                  />
                )}
              </div>
            </div>
          </div>
          <Paragraph style={{
            margin: 0,
            whiteSpace: 'pre-line',
            marginBottom: item.hasAttachment ? '12px' : 0
          }}>
            <Linkify text={item.content} />
          </Paragraph>

          {item.attachments && item.attachments.length > 0 && (
            <div style={{
              background: '#f9f9f9',
              padding: '8px',
              borderRadius: '4px',
              marginTop: '8px'
            }}>
              {item.attachments.map((file, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: idx < item.attachments!.length - 1 ? '8px' : 0,
                    fontSize: '13px'
                  }}
                >
                  <PaperClipOutlined style={{ marginRight: '8px', color: '#8c8c8c' }} />
                  <Text style={{ flex: 1 }} ellipsis>{file.name}</Text>
                  <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>
                    {file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(0)} KB` : `${(file.size / 1024 / 1024).toFixed(2)} MB`}
                  </Text>
                </div>
              ))}
            </div>
          )}

          {/* 展開エリア: 編集・削除ボタン */}
          {showActions && (
            <div className={`follow-history-actions${expandedFollowItems.has(item.id) ? ' expanded' : ''}`}>
              <div
                style={{
                  display: 'flex',
                  gap: '8px',
                  paddingTop: '10px',
                  borderTop: '1px dashed #e8e8e8'
                }}
              >
                {showDelete && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    style={{ flex: 1, height: '40px' }}
                    onClick={() => onDeleteClick(item.id)}
                  >
                    削除
                  </Button>
                )}
                {showEdit && (
                  <Button
                    type="default"
                    icon={<EditOutlined />}
                    style={{ flex: 1, height: '40px' }}
                    onClick={() => onEditClick(item.id)}
                  >
                    編集
                  </Button>
                )}
              </div>
            </div>
          )}
        </List.Item>
        );
      }}
    />
  );
};

export default FollowHistoryPanel;
